import React, { useState } from 'react';
import { Globe, Shield, CheckCircle2, PlaneTakeoff, Target, Building, Mail, Linkedin, Briefcase } from 'lucide-react';
import './Home.css';

const Home = () => {
  const [showContactCards, setShowContactCards] = useState(false);

  const services = [
    {
      icon: <Globe size={32} />,
      title: 'Pentest application Web',
      description: 'Tests black box / grey box sur applications web, focus sur failles réellement exploitables : auth/session, contrôle d\'accès (IDOR), injections, config, uploads, protections (rate limit, MFA, lockout) et logique métier.'
    },
    {
      icon: <Shield size={32} />,
      title: 'Pentest API',
      description: 'Contrôles d\'accès objet, schémas/paramètres/erreurs, mass assignment, pagination/filtrage, auth (JWT/OAuth), refresh/scopes, exposition de données.'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Cadrage',
      description: 'Périmètre, objectifs, règles d\'engagement, accès, fenêtre de test'
    },
    {
      number: '02',
      title: 'Reconnaissance & cartographie',
      description: 'Compréhension des flux, endpoints, rôles'
    },
    {
      number: '03',
      title: 'Tests manuels + appui automatisé',
      description: 'Priorisation par exploitabilité/impact'
    },
    {
      number: '04',
      title: 'Restitution',
      description: 'Synthèse exécutive + détails techniques + plan de remédiation'
    },
    {
      number: '05',
      title: 'Re-test (si prévu)',
      description: 'Validation des corrections'
    }
  ];

  const proofPoints = [];

  const experiences = [
    {
      company: 'Bonx',
      role: 'Pentester',
      period: 'Décembre 2025 - Janvier 2026 (1 mois)',
      location: '',
      tasks: [
        'Réalisation de missions de Bug Bounty sur les applications web et API de Bonx',
        'Identification et validation de vulnérabilités avec reporting détaillé et recommandations de mitigation',
        'Collaboration à distance avec les équipes techniques pour la correction des failles',
        'Méthodologie appliquée : tests manuels, scans automatisés, exploitation contrôlée et responsable'
      ],
      skills: ['Scan de vulnérabilité', 'Sécurité des API', 'GraphQL', 'Burp Suite', 'Rédaction de rapport']
    },
    {
      company: 'Nicomatic SA',
      role: 'Administrateur réseau & sécurité',
      period: 'Septembre 2024 - Septembre 2025 (1 an)',
      location: 'Annemasse, France',
      tasks: [
        'Administration et sécurisation d\'infrastructures : Active Directory, VMware vSphere/vCenter, pare-feux Sophos, VLANs',
        'Élaboration de scénarios d\'attaque ciblés, identification de failles de sécurité et mise en place de mesures correctives',
        'Mise en œuvre de contrôles d\'accès réseau avec déploiement du protocole 802.1X sur équipements industriels',
        'Analyse de trafic réseau avec Wireshark et supervision de la sécurité réseau',
        'Réalisation de tests de sécurité (brute force, analyse protocolaire) sur logiciels utilisés en production',
        'Scripting PowerShell pour automatisation de tâches administratives et durcissement de la configuration système'
      ],
      skills: ['Administration réseaux', 'Test d\'intrusion interne', 'Audit de sécurité', 'Evaluation des risques', 'Réseaux']
    },
    {
      company: 'Nicomatic India Bangalore',
      role: 'Administrateur réseau',
      period: 'Août 2023 - Novembre 2023 (3 mois)',
      location: 'Bangalore, Inde',
      tasks: [
        'Création d\'un plan d\'architecture réseau',
        'Détection et correction des principales failles de sécurité',
        'Maintenance du système d\'information',
        'Élaboration d\'une campagne de phishing'
      ],
      skills: ['Administration réseaux', 'Architecture IT', 'Administration système', 'Firewall', 'Adaptabilité et flexibilité']
    }
  ];

  const workModes = [
    {
      icon: <PlaneTakeoff size={32} />,
      title: 'Freelance',
      price: '325€/jour',
      description: 'Tarif journalier moyen pour des missions de pentest. Engagement flexible selon vos besoins.'
    },
    {
      icon: <Target size={32} />,
      title: 'Bug Bounty',
      price: 'Paiement au résultat',
      description: 'Rémunération en fonction de la gravité des failles découvertes. Pas de risque pour vous.'
    }
  ];

  const skills = [
    'Burp Suite',
    'Nmap',
    'Gobuster',
    'Kali Linux',
    'OWASP ZAP',
    'Wappalyzer',
    'Metasploit',
    'Wireshark'
  ];

  return (
    <main className="home">
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              Pentest Web & API
            </h1>
            <p className="hero-subtitle">
              Pentester à votre service. J'évalue vos applications web et vos API avec un angle "attaquant réel" 
              pour identifier les failles exploitables.
            </p>
          </div>
        </div>
      </section>

      <section className="section services-section">
        <div className="container">
          <h2 className="section-title">Services</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="parcours" className="section experience-section">
        <div className="container">
          <h2 className="section-title">Parcours</h2>
          <p className="section-subtitle">
            Expériences professionnelles en cybersécurité et administration systèmes.
          </p>
          <div className="experience-timeline">
            {experiences.map((exp, index) => (
              <div key={index} className="experience-item">
                <div className="experience-marker"></div>
                <div className="experience-content">
                  <div className="experience-header">
                    <div>
                      <h3 className="experience-role">{exp.role}</h3>
                      <h4 className="experience-company">
                        <Building size={18} />
                        {exp.company}
                      </h4>
                    </div>
                    <div className="experience-meta">
                      <span className="experience-period">{exp.period}</span>
                      {exp.location && <span className="experience-location">{exp.location}</span>}
                    </div>
                  </div>
                  <ul className="experience-tasks">
                    {exp.tasks.map((task, idx) => (
                      <li key={idx}>{task}</li>
                    ))}
                  </ul>
                  <div className="experience-skills">
                    {exp.skills.map((skill, idx) => (
                      <span key={idx} className="skill-badge">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="competences" className="section skills-section">
        <div className="container">
          <h2 className="section-title">Compétences</h2>
          <div className="skills-grid">
            {skills.map((skill, index) => (
              <div key={index} className="skill-card">
                <Shield size={24} className="skill-icon" />
                <span className="skill-name">{skill}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="processus" className="section process-section">
        <div className="container">
          <h2 className="section-title">Processus</h2>
          <p className="section-subtitle">
            Une approche structurée en 5 étapes pour des tests de sécurité efficaces.
          </p>
          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="mode-travail" className="section work-mode-section">
        <div className="container">
          <h2 className="section-title">Mode de travail</h2>
          <p className="section-subtitle">
            Deux formules adaptées à vos besoins et votre budget.
          </p>
          <div className="work-mode-grid">
            {workModes.map((mode, index) => (
              <div key={index} className="work-mode-card">
                <div className="work-mode-icon">{mode.icon}</div>
                <h3 className="work-mode-title">{mode.title}</h3>
                <p className="work-mode-price">{mode.price}</p>
                <p className="work-mode-description">{mode.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section contact-section">
        <div className="container">
          {!showContactCards ? (
            <div className="contact-cta-wrapper">
              <button 
                onClick={() => setShowContactCards(true)} 
                className="btn-primary btn-contact"
              >
                Me contacter
              </button>
            </div>
          ) : (
            <div className="contact-cards">
              <a href="mailto:vianney-s@hotmail.fr" className="contact-card">
                <Mail size={32} className="contact-card-icon" />
                <h3 className="contact-card-title">Email</h3>
                <p className="contact-card-text">vianney-s@hotmail.fr</p>
              </a>
              <a 
                href="https://www.linkedin.com/in/vianneysoulard" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-card"
              >
                <Linkedin size={32} className="contact-card-icon" />
                <h3 className="contact-card-title">LinkedIn</h3>
              </a>
              <a 
                href="https://www.malt.fr/profile/vianneysoulard1" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-card"
              >
                <Briefcase size={32} className="contact-card-icon" />
                <h3 className="contact-card-title">Malt</h3>
              </a>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Home;